import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { User, Lock, CreditCard, Trash2, AlertTriangle, Check, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { Button, Input, Modal } from '../components/ui';
import { UsageIndicator } from '../components/features';
import { useToast } from '../components/ui/Toast';
import { checkPasswordStrength, formatDate } from '../utils/helpers';

const SettingsPage = () => {
  const { user, profile, updateProfile, updatePassword, deleteAccount } = useAuth();
  const { isPro } = useChat();
  const { showToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile state
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'password', label: 'Password', icon: Lock },
    { id: 'plan', label: 'Plan & Billing', icon: CreditCard },
  ];

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await updateProfile({ display_name: displayName });
      showToast('Profile updated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update profile', 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await updatePassword(newPassword);
      showToast('Password updated successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showToast(error.message || 'Failed to update password', 'error');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    setIsDeleting(true);
    try {
      await deleteAccount();
      showToast('Account deleted', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to delete account', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const passwordStrength = checkPasswordStrength(newPassword);
  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-amber-500',
    strong: 'bg-green-500',
  };

  return (
    <>
      <Helmet>
        <title>Settings - CalcBuildAI</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            {/* Tabs */}
            <div className="border-b">
              <div className="flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-6 py-4 font-medium transition-colors
                      ${activeTab === tab.id 
                        ? 'text-blue-600 border-b-2 border-blue-600' 
                        : 'text-gray-500 hover:text-gray-700'
                      }
                    `}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Profile Information
                    </h2>
                    
                    <div className="space-y-4 max-w-md">
                      <Input
                        label="Display Name"
                        name="displayName"
                        placeholder="Your name"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                      />
                      
                      <Input
                        label="Email"
                        name="email"
                        value={user?.email || ''}
                        disabled
                        helperText="Email cannot be changed"
                      />
                      
                      <div className="text-sm text-gray-500">
                        <p>Account created: {formatDate(user?.created_at || new Date())}</p>
                      </div>
                      
                      <Button
                        onClick={handleSaveProfile}
                        loading={isSavingProfile}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>

                  <hr />

                  {/* Danger Zone */}
                  <div>
                    <h2 className="text-lg font-semibold text-red-600 mb-4">
                      Danger Zone
                    </h2>
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">Delete Account</h3>
                          <p className="text-sm text-gray-600">
                            Permanently delete your account and all data. This cannot be undone.
                          </p>
                        </div>
                        <Button
                          variant="danger"
                          onClick={() => setShowDeleteModal(true)}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Password Tab */}
              {activeTab === 'password' && (
                <div className="max-w-md">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Change Password
                  </h2>
                  
                  <form onSubmit={handleUpdatePassword} className="space-y-4">
                    <Input
                      type="password"
                      label="Current Password"
                      name="currentPassword"
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    
                    <div>
                      <Input
                        type="password"
                        label="New Password"
                        name="newPassword"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        helperText="At least 6 characters"
                        autoComplete="new-password"
                      />
                      
                      {newPassword.length > 0 && (
                        <div className="mt-2">
                          <div className="flex gap-1 mb-1">
                            <div className={`h-1 flex-1 rounded ${newPassword.length > 0 ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                            <div className={`h-1 flex-1 rounded ${passwordStrength !== 'weak' ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                            <div className={`h-1 flex-1 rounded ${passwordStrength === 'strong' ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                          </div>
                          <p className="text-xs text-gray-500 capitalize">
                            Strength: {passwordStrength}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <Input
                      type="password"
                      label="Confirm New Password"
                      name="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                    />
                    
                    <Button
                      type="submit"
                      loading={isUpdatingPassword}
                    >
                      Update Password
                    </Button>
                  </form>
                </div>
              )}

              {/* Plan & Billing Tab */}
              {activeTab === 'plan' && (
                <div className="space-y-6">
                  {/* Current Plan */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Current Plan
                    </h2>
                    
                    <div className={`p-6 rounded-xl border-2 ${isPro ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {isPro ? (
                            <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
                              <Sparkles className="w-6 h-6 text-white" />
                            </div>
                          ) : (
                            <div className="p-2 bg-gray-200 rounded-lg">
                              <User className="w-6 h-6 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              {isPro ? 'Pro Plan' : 'Free Plan'}
                            </h3>
                            <p className="text-gray-600">
                              {isPro ? 'Unlimited calculations' : '10 calculations per month'}
                            </p>
                          </div>
                        </div>
                        
                        {isPro ? (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">$12</p>
                            <p className="text-sm text-gray-500">/month</p>
                          </div>
                        ) : (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">$0</p>
                            <p className="text-sm text-gray-500">/month</p>
                          </div>
                        )}
                      </div>
                      
                      {!isPro && (
                        <Button
                          variant="gradient"
                          onClick={() => window.location.href = '/pricing'}
                          fullWidth
                        >
                          <Sparkles className="w-5 h-5" />
                          Upgrade to Pro
                        </Button>
                      )}
                      
                      {isPro && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            // TODO: Open Stripe Customer Portal
                            showToast('Stripe Customer Portal coming soon', 'info');
                          }}
                          fullWidth
                        >
                          Manage Subscription
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Usage */}
                  {!isPro && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Usage This Month
                      </h2>
                      <UsageIndicator showUpgrade={false} />
                    </div>
                  )}

                  {/* Pro Benefits */}
                  {!isPro && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Pro Benefits
                      </h2>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {[
                          'Unlimited calculations',
                          'Export to PDF',
                          'Priority support',
                          'No advertisements',
                          'Advanced cost breakdowns',
                          'Material price comparisons',
                        ].map((benefit, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-gray-700">
                            <Check className="w-5 h-5 text-green-500" />
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Account"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900">
                This action cannot be undone
              </h3>
              <p className="text-sm text-red-700">
                All your data including calculations, settings, and account 
                information will be permanently deleted.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <strong>DELETE</strong> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button 
              variant="ghost" 
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteAccount}
              loading={isDeleting}
              disabled={deleteConfirmText !== 'DELETE'}
            >
              Delete My Account
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SettingsPage;
